<template lang="pug">
    .communication-connections
        .table-top(v-if="communicationEnabled")
            el-button(
                v-if="showAddEmailButton"
                type="primary" plain size="mini" icon="el-icon-fa-plus fas"
                @click="openAddMailbox"
            ) {{ $t('Add email mailbox') }}
            el-tooltip(v-if="canCreateAccounts" :disabled="whatsappEnabled" :content="$t('Save the point of sale first.')" placement="top")
                span
                    el-button(
                        type="primary" plain size="mini" icon="el-icon-fa-plus fas"
                        :disabled="!whatsappEnabled"
                        @click="openCreate"
                    ) {{ $t('Add WhatsApp account') }}

        el-table.connections-table(:data="connections" v-loading="loading" @row-click="openConnection")
            el-table-column(:label="$t('Channel')" width="170")
                template(slot-scope="{ row }")
                    span.channel-cell
                        i.channel-cell__icon(:class="channelIcon(row)")
                        | {{ channelLabel(row) }}
            el-table-column(:label="$t('Name')" prop="name")
            el-table-column(:label="$t('Address / number')" prop="from_address")
                template(slot-scope="{ row }")
                    span {{ row.from_address || '-' }}
            el-table-column(:label="$t('Status')" width="180")
                template(slot-scope="{ row }")
                    el-tag(v-if="row.kind !== 'whatsapp' && !row.configured" size="mini" type="info" effect="plain") {{ $t('Not configured') }}
                    el-tag(v-else-if="row.kind === 'whatsapp' && row.has_access_token === false" size="mini" type="warning" effect="dark") {{ $t('Connection interrupted') }}
                    el-tag(v-else-if="row.is_validated" size="mini" type="success" effect="dark") {{ $t('Validated') }}
                    el-tag(v-else-if="row.is_faulty" size="mini" type="danger" effect="dark") {{ $t('Faulty') }}
                    el-tag(v-else size="mini" type="info" effect="plain") {{ $t('Not validated') }}
            el-table-column(width="120" align="right")
                template(slot-scope="{ row }")
                    template(v-if="row.kind === 'whatsapp'")
                        el-tooltip(v-if="canUpdateAccounts" :content="$t('Test connection')" placement="top")
                            el-button(
                                plain circle size="mini" type="primary"
                                icon="el-icon-fa-plug fas"
                                :loading="validatingId === row.id"
                                @click.stop="validate(row)"
                            )
                        el-tooltip(v-if="canDeleteAccounts" :content="$t('Delete')" placement="top")
                            el-button.delete-button(
                                plain circle size="mini" type="danger"
                                icon="el-icon-fa-trash-alt far"
                                @click.stop="confirmRemove(row)"
                            )
                    //- A POS's dedicated mailbox can be removed. The CMS shop mailbox
                    //- itself is not deletable here.
                    el-tooltip(
                        v-else-if="row.kind === 'email' && scope === 'pos' && row.configured"
                        :content="$t('Remove dedicated mailbox')" placement="top"
                    )
                        el-button.delete-button(
                            plain circle size="mini" type="danger"
                            icon="el-icon-fa-trash-alt far"
                            @click.stop="confirmRemoveMailbox"
                        )

        channel-account-form-drawer(
            ref="formDrawer"
            :visible.sync="drawer.visible"
            :account="drawer.account"
            :point-of-sale-id="pointOfSaleId"
            @saved="onSaved"
        )

        full-height-drawer(
            :visible.sync="mailboxDrawer.visible"
            :title="$t('Email mailbox')"
            size="620px"
            destroy-on-close
            @open="onMailboxOpen"
        )
            el-form.mailbox-form(label-position="top" :model="mailbox" :rules="mailboxRules" ref="mailboxForm")
                smtp-credentials(
                    :cms="syntheticCms"
                    scope="mailbox"
                    inline
                    :communication-enabled="communicationEnabled"
                    :show-signature="scope !== 'pos'"
                    :point-of-sale-id="pointOfSaleId"
                )
            template(#footer-left)
                el-button(round size="mini" icon="el-icon-fa-times fas" @click="cancelMailbox") {{ $t('Cancel') }}
            template(#footer)
                el-button(
                    round size="mini" type="success"
                    :icon="parentPersisted ? 'el-icon-fa-save fas' : 'el-icon-fa-check fas'"
                    @click="saveMailbox"
                ) {{ confirmLabel }}

        full-height-drawer(
            :visible.sync="newsletterDrawer.visible"
            :title="$t('Newsletter SMTP')"
            size="620px"
            destroy-on-close
            @open="onNewsletterOpen"
        )
            el-form.mailbox-form(label-position="top" :model="newsletterMailbox || {}" :rules="newsletterRules" ref="newsletterForm")
                smtp-credentials(
                    :cms="newsletterSyntheticCms"
                    scope="newsletterSmtp"
                    inline
                )
            template(#footer-left)
                el-button(round size="mini" icon="el-icon-fa-times fas" @click="cancelNewsletter") {{ $t('Cancel') }}
            template(#footer)
                el-button(
                    round size="mini" type="success"
                    :icon="parentPersisted ? 'el-icon-fa-save fas' : 'el-icon-fa-check fas'"
                    @click="saveNewsletter"
                ) {{ confirmLabel }}
</template>

<i18n>
{
    "de": {
        "Channel": "Kanal",
        "E-Mail": "E-Mail",
        "WhatsApp": "WhatsApp",
        "Email mailbox": "E-Mail-Postfach",
        "Address / number": "Adresse / Nummer",
        "Not configured": "Nicht konfiguriert",
        "Cancel": "Abbrechen",
        "Save": "Speichern",
        "Add WhatsApp account": "WhatsApp-Konto hinzufügen",
        "Add email mailbox": "E-Mail-Postfach hinzufügen",
        "Save the point of sale first.": "Speichere zuerst die Kasse.",
        "Refresh": "Aktualisieren",
        "Name": "Name",
        "Status": "Status",
        "Validated": "Verbunden",
        "Faulty": "Fehlerhaft",
        "Connection interrupted": "Verbindung unterbrochen",
        "Not validated": "Nicht geprüft",
        "Test connection": "Verbindung testen",
        "Delete": "Löschen",
        "Remove": "Entfernen",
        "Delete account": "Konto löschen",
        "This will permanently delete the WhatsApp account. Continue?": "Das WhatsApp-Konto wird dauerhaft gelöscht. Fortfahren?",
        "Remove dedicated mailbox": "Eigenes Postfach entfernen",
        "Remove dedicated mailbox?": "Eigenes Postfach entfernen?",
        "This point of sale will then have no email connection.": "Diese Kasse hat dann keine E-Mail-Verbindung.",
        "Connection validated": "Verbindung erfolgreich geprüft",
        "Connection failed": "Verbindung fehlgeschlagen",
        "Connect the account first. Click Connect and finish the sign-in.": "Verbinde zuerst das Konto. Klicke auf Verbinden und schließe die Anmeldung ab.",
        "Account deleted": "Konto gelöscht",
        "Something went wrong!": "Etwas ist schiefgelaufen!"
    }
}
</i18n>

<script>
import _ from 'lodash'
import { useCommunicationStore } from '@/stores'
import { checkRestrictions } from '@/helpers'

import ChannelAccountFormDrawer from '@/extensions/communication/views/ChannelAccount/FormDrawer'
import FullHeightDrawer from '@/components/FullHeightDrawer'
import SmtpCredentials from '@/views/settings/Account/Form/Partials/SmtpCredentials'
import ChannelAccountApi from '@/extensions/communication/store/api/channelAccount'
import emptyMailbox from '@/extensions/communication/utils/emptyMailbox'

export default {
    name: 'CommunicationConnections',
    components: {
        ChannelAccountFormDrawer,
        FullHeightDrawer,
        SmtpCredentials
    },
    props: {
        cmsId: {
            type: [Number, String],
            required: true
        },
        // The email mailbox object SmtpCredentials binds to inline (CMS: form.smtp;
        // POS: the POS form's mailbox). The host persists it on `request-save`.
        mailbox: {
            type: Object,
            required: true
        },
        // 'cms' = shop-level connections; 'pos' = scoped to one point of sale.
        scope: {
            type: String,
            default: 'cms'
        },
        // The saved POS id when scope==='pos' (null/undefined while unsaved).
        pointOfSaleId: {
            type: [Number, String],
            default: null
        },
        // The newsletter SMTP object (CMS scope only: form.newsletterSmtp). When a
        // newsletter mailbox is provided a "Newsletter" connection row is shown.
        newsletterMailbox: {
            type: Object,
            default: null
        },
        // Whether the newsletter channel applies (newsletter extension enabled).
        showNewsletter: {
            type: Boolean,
            default: false
        },
        // Whether the communication feature is active. When off, the WhatsApp rows
        // and the email mailbox's inbound/signature/POS sections are hidden — the
        // connections are pure SMTP senders (transactional + newsletter).
        communicationEnabled: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            accounts: [],
            loading: false,
            validatingId: null,
            drawer: { visible: false, account: null },
            mailboxDrawer: { visible: false },
            newsletterDrawer: { visible: false },
            // Snapshot taken when the mailbox drawer opens, so Cancel can discard the
            // edits SmtpCredentials writes live onto the mailbox object.
            mailboxSnapshot: null,
            newsletterSnapshot: null
        }
    },
    computed: {
        store() {
            return useCommunicationStore()
        },
        // The flags the API checks on ex:comm:accounts for the WhatsApp rows.
        canCreateAccounts() {
            return checkRestrictions(['ex:comm:accounts'], 'create')
        },
        canUpdateAccounts() {
            return checkRestrictions(['ex:comm:accounts'], 'update')
        },
        canDeleteAccounts() {
            return checkRestrictions(['ex:comm:accounts'], 'delete')
        },
        // SmtpCredentials reads/writes cms[scope]; expose the bound mailbox as cms.mailbox.
        syntheticCms() {
            return { id: this.cmsId, mailbox: this.mailbox }
        },
        // Same bridge for the send-only newsletter SMTP (scope='newsletterSmtp').
        newsletterSyntheticCms() {
            return { id: this.cmsId, newsletterSmtp: this.newsletterMailbox || {} }
        },
        // WhatsApp connect needs a real POS id when POS-scoped. CMS scope is always fine.
        whatsappEnabled() {
            if (this.scope !== 'pos') return true
            return !!this.pointOfSaleId && !String(this.pointOfSaleId).startsWith('new')
        },
        emailConfigured() {
            return !!_.get(this.mailbox, 'id')
        },
        // Is the PARENT (this POS, or the CMS) already persisted? Drives the whole
        // Save-vs-Ok behaviour: an unsaved parent can only APPLY the connection to its
        // in-memory form (persisted with the parent's first save); a persisted parent
        // SAVES just the connection now (a partial parent save) without touching the
        // parent's other, possibly-unsaved fields.
        parentPersisted() {
            const id = this.scope === 'pos' ? this.pointOfSaleId : this.cmsId
            return !!id && !String(id).startsWith('new')
        },
        // "Save" when the parent is persisted (we write the connection now), else "Ok"
        // (we only apply it to the parent form).
        confirmLabel() {
            return this.parentPersisted ? this.$t('Save') : this.$t('Ok')
        },
        // vee-validate (pipe-strings, via the VeeElement el-form bridge) for the mailbox
        // drawer. OAuth carries no SMTP fields — the sign-in IS the validation, and the
        // backend rejects an OAuth save with no token. Password/SMTP requires the send
        // leg; the password itself only for a NEW connection (an existing one keeps its
        // stored secret behind the "change password" switch).
        mailboxRules() {
            return this.connectionRules(this.mailbox)
        },
        newsletterRules() {
            return this.connectionRules(this.newsletterMailbox)
        },
        // At a POS, email is OPTIONAL: when none is set up we show a "+ Add email
        // mailbox" button instead of a "Not configured" row. The CMS always has its
        // transactional mailbox, so its email row is always present.
        showAddEmailButton() {
            return this.scope === 'pos' && !this.emailConfigured
        },
        connections() {
            const validation = _.get(this.mailbox, 'validation_status')
            const emailRow = {
                kind: 'email',
                name: _.get(this.mailbox, 'name') || _.get(this.mailbox, 'address') || this.$t('Email mailbox'),
                from_address: _.get(this.mailbox, 'address'),
                configured: !!_.get(this.mailbox, 'id'),
                is_validated: validation === 'valid',
                is_faulty: validation === 'invalid',
                is_active: true,
                pointsOfSale: _.get(this.mailbox, 'points_of_sale') || []
            }

            // At a POS, email is optional — omit the row when none is configured (the
            // "+ Add email mailbox" button is shown instead). The CMS always shows it.
            const rows = []
            if (this.scope !== 'pos' || emailRow.configured) {
                rows.push(emailRow)
            }

            if (this.showNewsletter) {
                const newsletterValidation = _.get(this.newsletterMailbox, 'validation_status')
                rows.push({
                    kind: 'newsletter',
                    name: _.get(this.newsletterMailbox, 'name') || _.get(this.newsletterMailbox, 'address') || this.$t('Newsletter'),
                    from_address: _.get(this.newsletterMailbox, 'address'),
                    configured: !!_.get(this.newsletterMailbox, 'id'),
                    is_validated: newsletterValidation === 'valid',
                    is_faulty: newsletterValidation === 'invalid',
                    is_active: true,
                    pointsOfSale: []
                })
            }

            // WhatsApp / messenger rows are a communication-feature concern.
            if (this.communicationEnabled) {
                const whatsapp = (this.accounts || [])
                    .filter(account => account.channel !== 'email')
                    .map(account => ({ ...account, kind: 'whatsapp' }))
                rows.push(...whatsapp)
            }

            return rows
        }
    },
    watch: {
        // Whatever happened in the WhatsApp drawer (connected, renamed, a template
        // created) is done once it closes: the page's other WhatsApp sections reload.
        'drawer.visible'(visible) {
            if (!visible) this.$emit('whatsapp-changed')
        }
    },
    mounted() {
        this.refresh()
    },
    methods: {
        channelIcon(row) {
            if (row.kind === 'whatsapp') return 'el-icon-fa-whatsapp fab'
            if (row.kind === 'newsletter') return 'el-icon-fa-paper-plane fas'
            return 'el-icon-fa-envelope fas'
        },
        channelLabel(row) {
            if (row.kind === 'whatsapp') return this.$t('WhatsApp')
            if (row.kind === 'newsletter') return this.$t('Newsletter')
            return this.$t('E-Mail')
        },
        // Pipe-string rules keyed by el-form-item prop (host/port/username/password on
        // SmtpCredentials). Empty for OAuth (no SMTP fields shown).
        connectionRules(model) {
            const connection = model || {}
            if (String(connection.auth_method || 'password').startsWith('oauth')) {
                return {}
            }
            const rules = {
                host: 'required',
                port: 'required|numeric',
                username: 'required'
            }
            // An existing connection keeps its stored secret; only a new one must type it.
            if (!connection.id) rules.password = 'required'
            return rules
        },
        refresh() {
            // WhatsApp accounts only exist with the communication feature on.
            if (!this.cmsId || !this.communicationEnabled) return
            // POS scope needs a REAL POS id. While it's unset (new/unsaved POS, or the
            // form hasn't loaded yet) there are no linked channels — show nothing.
            // NEVER fall back to the CMS-wide list (that's the inheritance we removed).
            if (this.scope === 'pos' && (!this.pointOfSaleId || String(this.pointOfSaleId).startsWith('new'))) {
                this.accounts = []
                return
            }
            this.loading = true
            ChannelAccountApi.fetchList(this.cmsId, this.scope === 'pos' ? this.pointOfSaleId : null)
                .then(accounts => { this.accounts = accounts || [] })
                .catch(() => this.notifyError(this.$t('Something went wrong!')))
                .finally(() => { this.loading = false })
        },
        openConnection(row) {
            if (row.kind === 'email') {
                this.mailboxDrawer.visible = true
            } else if (row.kind === 'newsletter') {
                this.newsletterDrawer.visible = true
            } else {
                this.openEdit(row)
            }
        },
        openCreate() {
            if (!this.whatsappEnabled) return
            this.drawer.account = null
            this.drawer.visible = true
        },
        openEdit(row) {
            this.drawer.account = _.cloneDeep(row)
            this.drawer.visible = true
        },
        // Opened from the order-notifications matrix ("Create template"): jump straight into
        // the connected WhatsApp account's drawer, where the template manager lives.
        openWhatsAppConnection(templatePreset = null) {
            const account = (this.accounts || []).find(item => item.channel !== 'email')
            if (!account) return

            this.openEdit({ ...account, kind: 'whatsapp' })

            // Opened to CREATE a template (the ticket section's and the order
            // notifications' shortcut): hand the preset to the manager once the
            // drawer has painted.
            if (templatePreset) {
                this.$nextTick(() => {
                    const drawer = this.$refs.formDrawer
                    if (drawer && drawer.openTemplatePreset) drawer.openTemplatePreset(templatePreset)
                })
            }
        },
        onSaved() {
            this.drawer.visible = false
            this.refresh()
        },
        // The "Add email mailbox" button always starts from a clean slate: discard any
        // half-built, never-saved connection left on the (unsaved-POS) mailbox model from
        // an earlier open, so the drawer never reappears already "Connected".
        openAddMailbox() {
            this.resetMailboxToEmpty()
            this.mailboxDrawer.visible = true
        },
        // Replace the bound mailbox with a fully-keyed empty one, in place (the host holds
        // this same ref). Fully-keyed so SmtpCredentials' field computeds stay reactive.
        resetMailboxToEmpty() {
            const fresh = emptyMailbox()
            Object.keys(this.mailbox).forEach(key => this.$delete(this.mailbox, key))
            Object.keys(fresh).forEach(key => this.$set(this.mailbox, key, _.cloneDeep(fresh[key])))
        },
        onMailboxOpen() {
            // A saved mailbox (has id) snapshots its persisted values so Cancel reverts
            // edits. A not-yet-saved one is a fresh "add" — its baseline is an EMPTY
            // mailbox, so Cancel discards the whole in-progress connection (including a
            // parked OAuth token) instead of restoring a half-built "connected" state.
            this.mailboxSnapshot = _.get(this.mailbox, 'id')
                ? _.cloneDeep(this.mailbox)
                : emptyMailbox()
            // Seeding the fields (reset-to-empty, or loading a saved mailbox) fires
            // el-form-item's change-validation, so a freshly opened drawer would show its
            // required fields already red. Clear the validation state once the form is
            // painted — the rules still fire on the next edit / on Save.
            this.$nextTick(() => {
                const form = this.$refs.mailboxForm
                if (form && form.clearValidate) form.clearValidate()
            })
        },
        cancelMailbox() {
            // Restore the pre-edit snapshot in place (the host holds this same ref).
            const snapshot = this.mailboxSnapshot || {}
            Object.keys(this.mailbox).forEach(key => this.$delete(this.mailbox, key))
            Object.keys(snapshot).forEach(key => this.$set(this.mailbox, key, _.cloneDeep(snapshot[key])))
            this.mailboxDrawer.visible = false
        },
        saveMailbox() {
            // Validate the SMTP fields (vee-validate) and require a finished OAuth sign-in.
            // Persisted parent → "Save": persist ONLY the mailbox now (the host does a
            // partial parent save). Unsaved parent → "Ok": the mailbox is already bound
            // inline into the parent form, so just close; it is written with the parent's
            // first save. Either way the parent form's OTHER fields are never saved here.
            this.$refs.mailboxForm.validate(valid => {
                if (!valid || !this.isConnectionReady(this.mailbox)) return
                if (this.parentPersisted) this.$emit('persist-mailbox')
                this.mailboxDrawer.visible = false
            })
        },
        onNewsletterOpen() {
            this.newsletterSnapshot = _.cloneDeep(this.newsletterMailbox)
            // Same as the mailbox drawer: don't show required fields red on a fresh open.
            this.$nextTick(() => {
                const form = this.$refs.newsletterForm
                if (form && form.clearValidate) form.clearValidate()
            })
        },
        cancelNewsletter() {
            const snapshot = this.newsletterSnapshot || {}
            const target = this.newsletterMailbox || {}
            Object.keys(target).forEach(key => this.$delete(target, key))
            Object.keys(snapshot).forEach(key => this.$set(target, key, _.cloneDeep(snapshot[key])))
            this.newsletterDrawer.visible = false
        },
        saveNewsletter() {
            // Same Save-vs-Ok gate as the mailbox drawer (newsletter is CMS-scoped, so the
            // parent is the persisted CMS → normally "Save" = persist just the newsletter).
            this.$refs.newsletterForm.validate(valid => {
                if (!valid || !this.isConnectionReady(this.newsletterMailbox)) return
                if (this.parentPersisted) this.$emit('persist-newsletter')
                this.newsletterDrawer.visible = false
            })
        },
        // A connection is ready to keep when it can actually be used. vee-validate above
        // covers the SMTP fields; OAuth is a STATE, not a field — an OAuth account must
        // have finished the sign-in (a parked token, a live connection, or an
        // already-validated saved channel). Notifies + blocks the close otherwise.
        isConnectionReady(model) {
            const connection = model || {}
            if (!String(connection.auth_method || 'password').startsWith('oauth')) {
                return true
            }
            const connected = connection.oauth_pending_token
                || connection.is_oauth_connected
                || (connection.id && connection.validation_status === 'valid')
            if (!connected) {
                this.notifyError(this.$t('Connect the account first. Click Connect and finish the sign-in.'))
                return false
            }
            return true
        },
        validate(row) {
            this.validatingId = row.id
            this.store.validateAccount(row.id)
                .then(updated => {
                    if (updated && updated.is_validated) {
                        this.notifySuccess(this.$t('Connection validated'))
                    } else {
                        this.notifyError(this.$t('Connection failed'))
                    }
                    this.refresh()
                })
                .catch(() => this.notifyError(this.$t('Connection failed')))
                .finally(() => { this.validatingId = null })
        },
        confirmRemove(row) {
            this.$confirm(
                this.$t('This will permanently delete the WhatsApp account. Continue?'),
                this.$t('Delete account'),
                {
                    confirmButtonText: this.$t('Delete'),
                    confirmButtonClass: 'el-button--danger',
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning'
                }
            ).then(() => this.remove(row)).catch(() => {})
        },
        remove(row) {
            this.store.deleteAccount(row.id)
                .then(() => {
                    this.notifySuccess(this.$t('Account deleted'))
                    this.refresh()
                    this.$emit('whatsapp-changed')
                })
                .catch(() => this.notifyError(this.$t('Something went wrong!')))
        },
        confirmRemoveMailbox() {
            this.$confirm(
                this.$t('This point of sale will then have no email connection.'),
                this.$t('Remove dedicated mailbox?'),
                {
                    confirmButtonText: this.$t('Remove'),
                    confirmButtonClass: 'el-button--danger',
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning'
                }
            ).then(() => this.removeMailbox()).catch(() => {})
        },
        removeMailbox() {
            // Same Save-vs-Ok split as saving. Persisted parent → persist the deletion now
            // (host does a partial parent save with the mailbox flagged deleted). Unsaved
            // parent → just drop it from the parent form (removed with the parent's save).
            // Strict POS isolation: no shop-mailbox fallback, so the POS then has no email.
            this.$set(this.mailbox, 'deleted', true)
            if (this.parentPersisted) this.$emit('delete-mailbox')
        },
        notifySuccess(title) {
            this.$notify.success({
                title,
                iconClass: 'fas el-icon-fa-check',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            })
        },
        notifyError(title) {
            this.$notify.error({
                title,
                iconClass: 'fas el-icon-fa-times',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000
            })
        }
    }
}
</script>

<style lang="sass" scoped>
.table-top
    display: flex
    gap: 10px
    margin-bottom: 15px
.connections-table
    ::v-deep .el-table__row
        cursor: pointer
    .channel-cell
        display: flex
        align-items: center
        &__icon
            width: 1.2em
            margin-right: 8px
            text-align: center
            color: $--color-primary
.mailbox-form
    // Hide the form's own "Email mailbox" heading - the drawer title already says it.
    ::v-deep .mailbox-heading
        display: none
</style>
